import React, {useMemo} from "react";
import {
    useTable,
    useSortBy,
    useGlobalFilter,
    useFilters,
    usePagination,
    useResizeColumns,
    useFlexLayout,
} from "react-table";
import {Button} from "react-bootstrap";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from '@mui/icons-material/Edit';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import {useTranslation} from "react-i18next";

interface DataTableProps {
    columns: any;
    data: any[];
    pageSize?: number;
    title: string;
    editable: boolean;
    deletable: boolean;
    alertsDeletable: boolean;
    onEdit?: (row: any) => void;
    onDelete?: (row: any) => void;
    onAlertsDelete?: (row: any) => void;
}

const DataTable: React.FC<DataTableProps> = ({
                                                 columns,
                                                 data,
                                                 title,
                                                 pageSize = 20,
                                                 editable = false,
                                                 onEdit,
                                                 deletable = false,
                                                 alertsDeletable = false,
                                                 onDelete,
                                                 onAlertsDelete,
                                             }) => {
    const {t} = useTranslation();
    const defaultColumn = useMemo(() => ({
        minWidth: 100,
        width: 150,
        maxWidth: 400,
        Filter: () => null
    }), []);
    const enhancedColumns = useMemo(() => {
        let cols = [...columns];
        if (editable || deletable) {
            cols.push({
                Header: t("RuleList.actions"),
                id: "actions",
                Cell: ({row}: any) => (
                    <div style={{display: "flex", gap: "10px"}}>
                        {editable && (
                            <Tooltip title={t('RuleList.edit')}>
                                <Button variant="outline-primary" size="sm" onClick={() => onEdit?.(row.original)}>
                                    <EditIcon fontSize="small"/>
                                </Button>
                            </Tooltip>
                        )}
                        {deletable && (
                            <Tooltip title={t('RuleList.delete')}>
                                <Button variant="outline-danger" size="sm" onClick={() => onDelete?.(row.original)}>
                                    <DeleteIcon fontSize="small"/>
                                </Button>
                            </Tooltip>
                        )}
                        {alertsDeletable && (
                            <Tooltip title={t('RuleList.delete_alerts')}>
                                <Button variant="outline-danger" size="sm"
                                        onClick={() => onAlertsDelete?.(row.original)}>
                                    <AlarmOffIcon fontSize="small"/>
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                ),
            });
        }
        return cols;
    }, [columns, editable, deletable, t, onEdit, onDelete, onAlertsDelete, alertsDeletable]);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        state: {pageIndex, globalFilter},
        setGlobalFilter,
    } = useTable(
        {
            columns: enhancedColumns,
            data,
            defaultColumn,
            initialState: {pageSize},
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useResizeColumns,
        useFlexLayout,
        usePagination
    );


    return (
        <React.Fragment>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 10,
                    background: "#fff",
                }}
            >
                <SearchIcon style={{color: "#888", fontSize: 20, marginRight: 8}}/>
                <input
                    value={globalFilter || ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={title}
                    style={{
                        border: "none",
                        outline: "none",
                        width: "100%",
                        fontSize: "14px",
                    }}
                />
            </div>

            <div {...getTableProps()} className="custom-header-css table align-middle table-nowrap"
                 style={{border: "1px solid #ccc", borderRadius: 4}}>
                <div>
                    {headerGroups.map((headerGroup: any) => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            style={{display: "flex", background: "#f8f9fa", fontWeight: "bold"}}
                        >
                            {headerGroup.headers.map((column: any, colIndex: number) => (
                                <div
                                    key={colIndex}
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    style={{
                                        flex: column.flex ?? 1,
                                        padding: "10px",
                                        borderRight: "1px solid #eee",
                                        position: "relative",
                                    }}
                                >
                                    {column.render("Header")}

                                    {column.isSorted && (
                                        column.isSortedDesc ? <ArrowDownwardIcon fontSize="small"/> :
                                            <ArrowUpwardIcon fontSize="small"/>
                                    )}

                                    <div
                                        {...column.getResizerProps()}
                                        style={{
                                            position: "absolute",
                                            right: 0,
                                            top: 0,
                                            height: "100%",
                                            width: "5px",
                                            cursor: "col-resize",
                                            userSelect: "none",
                                        }}
                                    />

                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div {...getTableBodyProps()}>
                    {page.map((row: any) => {
                        prepareRow(row);
                        return (
                            <div
                                {...row.getRowProps()}
                                style={{display: "flex", borderBottom: "1px solid #eee"}}
                            >
                                {row.cells.map((cell: any) => (
                                    <div
                                        {...cell.getCellProps()}
                                        style={{flex: cell.column.flex ?? 1, padding: "10px"}}
                                    >
                                        {cell.render("Cell")}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{marginTop: 16, display: "flex", gap: 8, alignItems: "center"}}>
                <Button className="btn-icon" variant="primary" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    <i className="ri-skip-back-fill"></i>
                </Button>
                <Button className="btn-icon" variant="primary" onClick={() => previousPage()}
                        disabled={!canPreviousPage}>
                    <i className="ri-arrow-left-fill"></i>
                </Button>
                <span>
             <strong>{pageIndex + 1}</strong> / <strong>{pageOptions.length}</strong>
        </span>
                <Button className="btn-icon" variant="primary" onClick={() => nextPage()} disabled={!canNextPage}>
                    <i className="ri-arrow-right-fill"></i>
                </Button>
                <Button className="btn-icon" variant="primary" onClick={() => gotoPage(pageCount - 1)}
                        disabled={!canNextPage}>
                    <i className="ri-skip-forward-fill"></i>
                </Button>
            </div>
        </React.Fragment>
    );
};

export default DataTable;
